import React, { useState, useEffect, useMemo } from 'react';
import { getVitamins, createVitamin, updateVitamin, deleteVitamin, getMedications, createMedication, updateMedication, deleteMedication, getBloodResults } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, Pill, Leaf, Sparkles, Edit2, X, Calendar, Activity } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useReadOnly } from '../context/ReadOnlyContext';
import SupplementTimeline from '../components/SupplementTimeline';
import TimeWindowSelector, { filterTreatmentsByTimeWindow } from '../components/TimeWindowSelector';

/**
 * Unified Treatments Page
 * Combines Vitamins, Supplements, and Medications into one view
 * Layout matches the demo/preview style with timeline at top
 */
const TreatmentsPage = () => {
  const { readonlyToken } = useReadOnly();
  const [vitamins, setVitamins] = useState([]);
  const [medications, setMedications] = useState([]);
  const [bloodResults, setBloodResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeWindow, setTimeWindow] = useState('36m'); // Time window for timeline - default 36 months
  
  // Create state
  const [isCreating, setIsCreating] = useState(false);
  const [createCategory, setCreateCategory] = useState('vitamin'); // 'vitamin', 'supplement', or 'medication'
  const [createFormData, setCreateFormData] = useState({
    name: '',
    dosage: '',
    frequency: '',
    start_date: '',
    end_date: '',
    notes: ''
  });
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    dosage: '',
    frequency: '',
    start_date: '',
    end_date: '',
    notes: '',
    category: 'vitamin'
  });

  useEffect(() => {
    fetchAllData();
  }, [readonlyToken]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [vitaminsData, medicationsData, bloodData] = await Promise.all([
        getVitamins().catch(() => []),
        getMedications().catch(() => []),
        getBloodResults().catch(() => [])
      ]);
      setVitamins(vitaminsData);
      setMedications(medicationsData);
      setBloodResults(bloodData);
    } catch (error) {
      toast.error('Failed to load treatments');
    } finally {
      setLoading(false);
    }
  };

  // Combine all treatments for timeline
  const allTreatments = useMemo(() => {
    const vitaminItems = vitamins.map(v => ({
      ...v,
      // Preserve the actual category from the database (vitamin or supplement)
      category: v.category || 'vitamin',
      type: 'vitamin'  // type is used for API calls (vitamins table)
    }));
    const medicationItems = medications.map(m => ({
      ...m,
      category: 'medication',
      type: 'medication'
    }));
    return [...vitaminItems, ...medicationItems].sort((a, b) => 
      new Date(b.start_date) - new Date(a.start_date)
    );
  }, [vitamins, medications]);

  // Filter treatments by selected time window
  const filteredTreatments = useMemo(() => {
    return filterTreatmentsByTimeWindow(allTreatments, bloodResults, timeWindow);
  }, [allTreatments, bloodResults, timeWindow]);

  // Calculate axis config for timeline based on time window
  const axisConfig = useMemo(() => {
    if (filteredTreatments.length === 0) return null;
    
    // For time-windowed views, domain ends at TODAY
    const now = Date.now();
    let domainStart, domainEnd;
    
    if (timeWindow === 'all') {
      // For 'all', use the full date range of treatments
      const allDates = [
        ...filteredTreatments.map(t => new Date(t.start_date).getTime()),
        ...filteredTreatments.filter(t => t.end_date).map(t => new Date(t.end_date).getTime())
      ].filter(d => !isNaN(d));
      
      if (allDates.length === 0) return null;
      
      domainStart = Math.min(...allDates);
      domainEnd = Math.max(...allDates, now);
    } else {
      // For 12M, 24M, 36M - fixed window ending at today
      const months = timeWindow === '12m' ? 12 : timeWindow === '24m' ? 24 : 36;
      domainEnd = now;
      const startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - months);
      domainStart = startDate.getTime();
    }
    
    return {
      dataPoints: [],
      domainStartTs: domainStart,
      domainEndTs: domainEnd,
      margin: { left: 45, right: 20 }
    };
  }, [filteredTreatments, timeWindow]);

  // Category helpers
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'medication': return Pill;
      case 'supplement': return Leaf;
      case 'vitamin': return Sparkles;
      case 'other': return Pill;
      default: return Pill;
    }
  };

  const getCategoryColor = (category) => {
    // Match EXACT colors from SupplementTimeline for consistency
    switch (category) {
      case 'medication': return { 
        bg: 'bg-indigo-100', 
        text: 'text-indigo-700', 
        border: 'border-indigo-200', 
        icon: 'text-indigo-600',
        hex: '#4F6BED',
        badgeBg: 'bg-[#4F6BED]',
        badgeText: 'text-white'
      };
      case 'supplement': return { 
        bg: 'bg-emerald-100', 
        text: 'text-emerald-700', 
        border: 'border-emerald-200', 
        icon: 'text-emerald-600',
        hex: '#3FAE7F',
        badgeBg: 'bg-[#3FAE7F]',
        badgeText: 'text-white'
      };
      case 'vitamin': return { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-800', 
        border: 'border-yellow-300', 
        icon: 'text-yellow-600',
        hex: '#F2B705',
        badgeBg: 'bg-[#F2B705]',
        badgeText: 'text-gray-900'
      };
      case 'other': return { 
        bg: 'bg-purple-100', 
        text: 'text-purple-700', 
        border: 'border-purple-200', 
        icon: 'text-purple-600',
        hex: '#8B5CF6',
        badgeBg: 'bg-[#8B5CF6]',
        badgeText: 'text-white'
      };
      default: return { 
        bg: 'bg-slate-50', 
        text: 'text-slate-700', 
        border: 'border-slate-200', 
        icon: 'text-slate-600',
        hex: '#64748b',
        badgeBg: 'bg-slate-500',
        badgeText: 'text-white'
      };
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'medication': return 'Medication';
      case 'supplement': return 'Supplement';
      case 'vitamin': return 'Vitamin';
      case 'other': return 'Other';
      default: return 'Treatment';
    }
  };

  // Create handlers
  const handleCreate = (category = 'vitamin') => {
    setCreateCategory(category);
    setCreateFormData({
      name: '',
      dosage: '',
      frequency: '',
      start_date: '',
      end_date: '',
      notes: ''
    });
    setIsCreating(true);
  };

  const handleSaveCreate = async (e) => {
    e.preventDefault();
    
    if (!createFormData.name || !createFormData.dosage || !createFormData.frequency || !createFormData.start_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    const payload = {
      name: createFormData.name,
      dosage: createFormData.dosage,
      frequency: createFormData.frequency,
      start_date: createFormData.start_date,
      end_date: createFormData.end_date || null,
      notes: createFormData.notes || null
    };

    try {
      if (createCategory === 'medication') {
        await createMedication(payload);
        toast.success('Medication added');
      } else {
        await createVitamin(payload);
        toast.success(`${getCategoryLabel(createCategory)} added`);
      }
      setIsCreating(false);
      await fetchAllData();
    } catch (error) {
      toast.error('Failed to add treatment');
    }
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
  };

  // Edit handlers
  const handleEdit = async (item) => {
    try {
      let freshData;
      if (item.type === 'medication') {
        const allMeds = await getMedications();
        freshData = allMeds.find(m => m.id === item.id);
      } else {
        const allVits = await getVitamins();
        freshData = allVits.find(v => v.id === item.id);
      }
      
      if (!freshData) {
        toast.error('Record not found');
        return;
      }
      
      setEditingItem({ ...freshData, type: item.type, category: item.category });
      setEditFormData({
        name: freshData.name || '',
        dosage: freshData.dosage || '',
        frequency: freshData.frequency || '',
        start_date: freshData.start_date || '',
        end_date: freshData.end_date || '',
        notes: freshData.notes || '',
        category: item.category || 'vitamin'
      });
      setIsEditing(true);
    } catch (error) {
      toast.error('Failed to load record for editing');
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.name || !editFormData.dosage || !editFormData.frequency || !editFormData.start_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    const payload = {
      name: editFormData.name,
      dosage: editFormData.dosage,
      frequency: editFormData.frequency,
      start_date: editFormData.start_date,
      end_date: editFormData.end_date || null,
      notes: editFormData.notes || null,
      category: editFormData.category
    };

    try {
      if (editingItem.type === 'medication') {
        await updateMedication(editingItem.id, payload);
        toast.success('Medication updated');
      } else {
        await updateVitamin(editingItem.id, payload);
        toast.success('Treatment updated');
      }
      setIsEditing(false);
      setEditingItem(null);
      await fetchAllData();
    } catch (error) {
      toast.error('Failed to update treatment');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingItem(null);
  };

  // Delete handler
  const handleDelete = async (item) => {
    const label = getCategoryLabel(item.category);
    if (!window.confirm(`Are you sure you want to delete this ${label.toLowerCase()}?`)) return;
    
    try {
      if (item.type === 'medication') {
        await deleteMedication(item.id);
      } else {
        await deleteVitamin(item.id);
      }
      toast.success(`${label} deleted`);
      await fetchAllData();
    } catch (error) {
      toast.error('Failed to delete treatment');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading treatments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-2">
            Treatments
          </h1>
          <p className="text-base sm:text-lg leading-relaxed text-slate-700">Track your medications, supplements, and vitamins</p>
        </div>
        <Button
          onClick={() => handleCreate('vitamin')}
          className="bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 text-sm sm:text-base"
        >
          <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
          Add Treatment
        </Button>
      </div>

      {/* Timeline Section */}
      {allTreatments.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Treatment Timeline</h2>
                <p className="text-sm text-slate-500 mt-1">Visualize when treatments started and ended</p>
              </div>
              <div className="self-end sm:self-auto">
                <TimeWindowSelector 
                  selected={timeWindow} 
                  onChange={setTimeWindow} 
                />
              </div>
            </div>
          </div>
          <div className="p-4">
            <SupplementTimeline
              supplements={filteredTreatments}
              axisConfig={axisConfig}
              maxHeight={250}
            />
          </div>
        </div>
      )}

      {/* CREATE - Modal on desktop, Full screen on mobile (below header) */}
      {isCreating && (
        <div className="fixed inset-0 z-50 flex items-start lg:items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={handleCancelCreate} />
          
          {/* Form Container - Full screen on mobile, modal on desktop */}
          <div className="relative w-full h-full lg:h-auto lg:max-h-[85vh] lg:w-full lg:max-w-lg bg-white lg:rounded-2xl lg:shadow-xl overflow-y-auto flex flex-col">
            {/* Mobile close button - simple X at top right */}
            <div className="lg:hidden flex justify-end px-4 pt-6 flex-shrink-0">
              <button
                type="button"
                onClick={handleCancelCreate}
                className="p-2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="px-4 pb-6 lg:p-5 flex-1 flex flex-col">
              {/* Desktop header */}
              <div className="hidden lg:flex items-center gap-2 mb-3">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 flex-shrink-0">
                  <Pill className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-900">Add Treatment</h1>
                  <p className="text-[11px] text-slate-500">Track vitamins, supplements, or medications</p>
                </div>
              </div>

              <form onSubmit={handleSaveCreate} className="flex-1 flex flex-col lg:block lg:space-y-2.5">
                {/* Form fields wrapper - spreads on mobile */}
                <div className="flex-1 flex flex-col justify-evenly lg:justify-start lg:space-y-2.5 space-y-3 lg:space-y-2.5">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Type *</label>
                    <div className="grid grid-cols-4 gap-1.5">
                      {['vitamin', 'supplement', 'medication', 'other'].map((cat) => {
                        const Icon = getCategoryIcon(cat);
                        const colors = getCategoryColor(cat);
                        const isSelected = createCategory === cat;
                        return (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setCreateCategory(cat)}
                            className={`p-2 lg:p-1.5 rounded-lg border-2 transition-all flex flex-col items-center gap-1 lg:gap-0.5 ${
                              isSelected 
                                ? `${colors.border} ${colors.bg}` 
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <Icon className={`h-4 w-4 lg:h-3.5 lg:w-3.5 ${isSelected ? colors.icon : 'text-slate-400'}`} />
                            <span className={`text-[10px] lg:text-[9px] font-medium capitalize ${isSelected ? colors.text : 'text-slate-600'}`}>
                              {cat}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Name *</label>
                    <input
                      type="text"
                      value={createFormData.name}
                      onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                      required
                      className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 lg:px-2.5 lg:py-1.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      placeholder={createCategory === 'medication' ? 'e.g., Ibrutinib' : 'e.g., Vitamin D3'}
                    />
                  </div>

                  {/* Dosage & Frequency - Side by side */}
                  <div className="grid grid-cols-2 gap-3 lg:gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Dosage *</label>
                      <input
                        type="text"
                        value={createFormData.dosage}
                        onChange={(e) => setCreateFormData({ ...createFormData, dosage: e.target.value })}
                        required
                        className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 lg:px-2.5 lg:py-1.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        placeholder={createCategory === 'medication' ? 'e.g., 420mg' : 'e.g., 2000 IU'}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Frequency *</label>
                      <input
                        type="text"
                        value={createFormData.frequency}
                        onChange={(e) => setCreateFormData({ ...createFormData, frequency: e.target.value })}
                        required
                        className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 lg:px-2.5 lg:py-1.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        placeholder="e.g., Once daily"
                      />
                    </div>
                  </div>

                  {/* Dates - Side by side */}
                  <div className="grid grid-cols-2 gap-3 lg:gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Start Date *</label>
                      <input
                        type="date"
                        value={createFormData.start_date}
                        onChange={(e) => setCreateFormData({ ...createFormData, start_date: e.target.value })}
                        max={new Date().toISOString().split('T')[0]}
                        required
                        className="w-full h-[44px] lg:h-[34px] text-sm text-slate-900 bg-slate-50 rounded-lg px-3 lg:px-2.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">End Date</label>
                      <input
                        type="date"
                        value={createFormData.end_date}
                        onChange={(e) => setCreateFormData({ ...createFormData, end_date: e.target.value })}
                        className="w-full h-[44px] lg:h-[34px] text-sm text-slate-900 bg-slate-50 rounded-lg px-3 lg:px-2.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Notes (optional)</label>
                    <textarea
                      value={createFormData.notes}
                      onChange={(e) => setCreateFormData({ ...createFormData, notes: e.target.value })}
                      rows={2}
                      className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 lg:px-2.5 lg:py-1.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
                      placeholder="Additional details..."
                    />
                  </div>
                </div>

                {/* Buttons - at bottom with spacing */}
                <div className="flex gap-3 lg:gap-2 pt-4 lg:pt-2 pb-safe mt-auto lg:mt-0">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCancelCreate}
                    className="flex-1 h-12 lg:h-10"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 h-12 lg:h-10">
                    Add {getCategoryLabel(createCategory)}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* EDIT - Modal on desktop, Full screen on mobile (below header) */}
      {isEditing && editingItem && (
        <div className="fixed inset-0 z-50 flex items-start lg:items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={handleCancelEdit} />
          
          {/* Form Container - Full screen on mobile, modal on desktop */}
          <div className="relative w-full h-full lg:h-auto lg:max-h-[85vh] lg:w-full lg:max-w-lg bg-white lg:rounded-2xl lg:shadow-xl overflow-y-auto flex flex-col">
            {/* Mobile close button - simple X at top right */}
            <div className="lg:hidden flex justify-end px-4 pt-6 flex-shrink-0">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="p-2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="px-4 pb-6 lg:p-5 flex-1 flex flex-col">
              {/* Desktop header */}
              <div className="hidden lg:flex items-center gap-2 mb-3">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 flex-shrink-0">
                  <Pill className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-900">Edit Treatment</h1>
                  <p className="text-[11px] text-slate-500">Update your treatment details</p>
                </div>
              </div>

              <form onSubmit={handleSaveEdit} className="flex-1 flex flex-col lg:block lg:space-y-2.5">
                {/* Form fields wrapper - spreads on mobile */}
                <div className="flex-1 flex flex-col justify-evenly lg:justify-start lg:space-y-2.5 space-y-3 lg:space-y-2.5">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Type *</label>
                    <div className="grid grid-cols-4 gap-1.5">
                      {['vitamin', 'supplement', 'medication', 'other'].map((cat) => {
                        const Icon = getCategoryIcon(cat);
                        const colors = getCategoryColor(cat);
                        const isSelected = editFormData.category === cat;
                        return (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setEditFormData({ ...editFormData, category: cat })}
                            className={`p-2 lg:p-1.5 rounded-lg border-2 transition-all flex flex-col items-center gap-1 lg:gap-0.5 ${
                              isSelected 
                                ? `${colors.border} ${colors.bg}` 
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <Icon className={`h-4 w-4 lg:h-3.5 lg:w-3.5 ${isSelected ? colors.icon : 'text-slate-400'}`} />
                            <span className={`text-[10px] lg:text-[9px] font-medium capitalize ${isSelected ? colors.text : 'text-slate-600'}`}>
                              {cat}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Name *</label>
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      required
                      className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 lg:px-2.5 lg:py-1.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                  </div>

                  {/* Dosage & Frequency - Side by side */}
                  <div className="grid grid-cols-2 gap-3 lg:gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Dosage *</label>
                      <input
                        type="text"
                        value={editFormData.dosage}
                        onChange={(e) => setEditFormData({ ...editFormData, dosage: e.target.value })}
                        required
                        className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 lg:px-2.5 lg:py-1.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Frequency *</label>
                      <input
                        type="text"
                        value={editFormData.frequency}
                        onChange={(e) => setEditFormData({ ...editFormData, frequency: e.target.value })}
                        required
                        className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 lg:px-2.5 lg:py-1.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      />
                    </div>
                  </div>

                  {/* Dates - Side by side */}
                  <div className="grid grid-cols-2 gap-3 lg:gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Start Date *</label>
                      <input
                        type="date"
                        value={editFormData.start_date}
                        onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
                        max={new Date().toISOString().split('T')[0]}
                        required
                        className="w-full h-[44px] lg:h-[34px] text-sm text-slate-900 bg-slate-50 rounded-lg px-3 lg:px-2.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">End Date</label>
                      <input
                        type="date"
                        value={editFormData.end_date}
                        onChange={(e) => setEditFormData({ ...editFormData, end_date: e.target.value })}
                        className="w-full h-[44px] lg:h-[34px] text-sm text-slate-900 bg-slate-50 rounded-lg px-3 lg:px-2.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Notes (optional)</label>
                    <textarea
                      value={editFormData.notes}
                      onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                      rows={2}
                      className="w-full text-sm text-slate-900 bg-slate-50 rounded-lg px-3 py-2.5 lg:px-2.5 lg:py-1.5 border border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
                      placeholder="Additional details..."
                    />
                  </div>
                </div>

                {/* Buttons - at bottom with spacing */}
                <div className="flex gap-3 lg:gap-2 pt-4 lg:pt-2 pb-safe mt-auto lg:mt-0">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCancelEdit}
                    className="flex-1 h-12 lg:h-10"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 h-12 lg:h-10">
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Treatment Cards */}
      {allTreatments.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 border border-slate-100 shadow-sm text-center">
          <Pill className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No treatments yet</h3>
          <p className="text-slate-600 mb-6">Start tracking by adding your first vitamin, supplement, or medication</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {allTreatments.map(treatment => {
            const Icon = getCategoryIcon(treatment.category);
            const colors = getCategoryColor(treatment.category);
            const isOngoing = !treatment.end_date;

            return (
              <div 
                key={`${treatment.type}-${treatment.id}`}
                className={`bg-white rounded-xl border ${colors.border} p-5 hover:shadow-md transition-shadow`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${colors.bg} rounded-lg`}>
                      <Icon className={`h-5 w-5 ${colors.icon}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{treatment.name}</h3>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badgeBg} ${colors.badgeText}`}>
                        {getCategoryLabel(treatment.category)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isOngoing && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                        Active
                      </span>
                    )}
                    <button
                      onClick={() => handleEdit(treatment)}
                      className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(treatment)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span>Started: {formatDate(treatment.start_date)}</span>
                  </div>
                  {treatment.end_date && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span>Ended: {formatDate(treatment.end_date)}</span>
                    </div>
                  )}
                  {treatment.dosage && (
                    <p className="text-slate-600">
                      <span className="text-slate-400">Dosage:</span> {treatment.dosage}
                    </p>
                  )}
                  {treatment.frequency && (
                    <p className="text-slate-600">
                      <span className="text-slate-400">Frequency:</span> {treatment.frequency}
                    </p>
                  )}
                  {treatment.notes && (
                    <p className="text-slate-500 italic mt-2">{treatment.notes}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TreatmentsPage;
